import { identity } from "lodash";
import { makeAutoObservable } from "mobx";
import { DecisionContext } from "@/dbot/common/commonDomain";
import { DecisionType } from "@/common/decisions";
import {
  BooleanFieldModel,
  NumberFieldModel,
  OptionFieldModel,
  RadioFieldModel,
  StringFieldModel,
} from "@/common/commonForms";
import { ShareType } from "@/common/share";

export type ConvertibleLoanPowerOfAttorney = {
  type: DecisionType.convertibleLoanPowerOfAttorney;
  convertibleLoanValue: number;
  convertibleLoanCapitalShareValue: number;
  convertibleLoanDuration: ConvertibleLoanDuration;
  convertibleLoanDeviateFromShareholderRights: boolean;
  convertibleLoanShareTypes: string[];
  convertibleLoanShareTypeIds?: number[];
  isConvertibleLoanAllShareTypes: boolean;
  convertibleLoanNewShareType: boolean;
  convertibleLoanNewShareTypeDescription?: string;
  convertibleLoanReason: string;
  convertibleLoanDurationMonths?: number;
};

export enum ConvertibleLoanDuration {
  UNTIL_GM = "untilGM",
  MONTHS = "months",
}

export class ConvertibleLoanPowerOfAttorneyForm {
  readonly type = DecisionType.convertibleLoanPowerOfAttorney;

  context: DecisionContext<ConvertibleLoanPowerOfAttorney>;
  data?: ConvertibleLoanPowerOfAttorney;

  convertibleLoanValue: NumberFieldModel;
  convertibleLoanCapitalShareValue: NumberFieldModel;
  convertibleLoanDuration: RadioFieldModel<ConvertibleLoanDuration>;
  convertibleLoanDeviateFromShareholderRights: BooleanFieldModel;
  convertibleLoanShareTypes: OptionFieldModel<ShareType>;
  convertibleLoanNewShareType: BooleanFieldModel;
  convertibleLoanNewShareTypeDescription: StringFieldModel;
  convertibleLoanReason: StringFieldModel;
  convertibleLoanDurationMonths: NumberFieldModel;

  constructor(
    context: DecisionContext<ConvertibleLoanPowerOfAttorney>,
    data?: ConvertibleLoanPowerOfAttorney,
  ) {
    makeAutoObservable(this, {}, { autoBind: true });
    this.context = context;
    this.data = data;

    this.convertibleLoanValue = new NumberFieldModel({
      value: data?.convertibleLoanValue,
      isInteger: true,
      isPositive: true,
      required: true,
    });
    this.convertibleLoanCapitalShareValue = new NumberFieldModel({
      value: data?.convertibleLoanCapitalShareValue,
      isInteger: true,
      isPositiveOrZero: true,
      required: true,
    });
    this.convertibleLoanDuration = new RadioFieldModel<ConvertibleLoanDuration>({
      value: data?.convertibleLoanDuration ?? ConvertibleLoanDuration.UNTIL_GM,
      options: Object.values(ConvertibleLoanDuration),
      idFn: identity,
      labelFn: (v) => ({ key: `decisions.convertibleLoanPoA.convertibleLoanDuration.${v}` }),
    });
    this.convertibleLoanDeviateFromShareholderRights = new BooleanFieldModel({
      value: data?.convertibleLoanDeviateFromShareholderRights,
    });
    this.convertibleLoanShareTypes = new OptionFieldModel({
      value: data?.convertibleLoanShareTypeIds
        ? this.shareTypes.filter((st) => data.convertibleLoanShareTypeIds?.includes(st.sharetypeId))
        : data?.convertibleLoanShareTypes
          ? this.shareTypes.filter((st) =>
              data.convertibleLoanShareTypes?.includes(st.shareTypeCode),
            )
          : undefined,
      options: this.shareTypes,
      idFn: (item) => item.sharetypeId.toString(),
      labelFn: () => ({ translated: "" }),
    });
    this.convertibleLoanNewShareType = new BooleanFieldModel({
      value: data?.convertibleLoanNewShareType,
    });
    this.convertibleLoanNewShareTypeDescription = new StringFieldModel({
      value: data?.convertibleLoanNewShareTypeDescription,
    });
    this.convertibleLoanReason = new StringFieldModel({
      value: data?.convertibleLoanReason,
    });
    this.convertibleLoanDurationMonths = new NumberFieldModel({
      value: data?.convertibleLoanDurationMonths,
      isPositive: true,
      isInteger: true,
    });
  }

  toggleAllShareTypes() {
    if (this.isAllShareTypesSelected) {
      this.convertibleLoanShareTypes.clear();
    } else {
      this.convertibleLoanShareTypes.selectAll();
    }
  }

  validate(): boolean {
    this.convertibleLoanValue.validate();
    this.convertibleLoanCapitalShareValue.validate();
    this.convertibleLoanDeviateFromShareholderRights.validate();
    this.convertibleLoanNewShareType.validate();
    this.convertibleLoanNewShareTypeDescription.validate();
    this.convertibleLoanReason.validate();
    this.convertibleLoanDurationMonths.validate();
    return this.isValid;
  }

  get isAllShareTypesSelected(): boolean {
    return (
      this.convertibleLoanShareTypes.selected.length ===
      this.convertibleLoanShareTypes.options.length
    );
  }

  get isShareTypesExpanded(): boolean {
    return (
      this.convertibleLoanShareTypes.selected.length !== 0 &&
      this.convertibleLoanShareTypes.selected.length !==
        this.convertibleLoanShareTypes.options.length
    );
  }

  get isConvertibleLoanValueMoreThanZero(): boolean {
    return !!this.convertibleLoanValue.parsedValue && this.convertibleLoanValue.parsedValue > 0;
  }

  get isConvertibleLoanCapitalShareValueMoreThanZero(): boolean {
    return (
      !!this.convertibleLoanCapitalShareValue.parsedValue &&
      this.convertibleLoanCapitalShareValue.parsedValue > 0
    );
  }

  get invalidDurationMonths(): boolean {
    return Boolean(
      this.convertibleLoanDurationMonths.parsedValue &&
      (this.convertibleLoanDurationMonths.parsedValue > 24 ||
        this.convertibleLoanDurationMonths.parsedValue < 0),
    );
  }

  get checkShareTypes(): boolean {
    return Boolean(
      (this.convertibleLoanShareTypes.selected.length > 0 &&
        !this.convertibleLoanNewShareType.value) ||
      (this.convertibleLoanNewShareType.value &&
        this.convertibleLoanNewShareTypeDescription.validatedValue),
    );
  }

  get showNewShareTypeDescriptionInput(): boolean {
    return (
      this.isConvertibleLoanValueMoreThanZero &&
      this.isConvertibleLoanCapitalShareValueMoreThanZero &&
      !!this.convertibleLoanNewShareType.value
    );
  }

  get showShareholderRightsQuestion(): boolean {
    return (
      this.isConvertibleLoanValueMoreThanZero &&
      this.isConvertibleLoanCapitalShareValueMoreThanZero &&
      this.checkShareTypes
    );
  }

  get showDurationQuestion(): boolean {
    return Boolean(
      this.isConvertibleLoanValueMoreThanZero &&
      this.isConvertibleLoanCapitalShareValueMoreThanZero &&
      this.checkShareTypes &&
      this.convertibleLoanDeviateFromShareholderRights.value !== undefined,
    );
  }

  get showDurationMonthsInput(): boolean {
    return Boolean(
      this.isConvertibleLoanValueMoreThanZero &&
      this.isConvertibleLoanCapitalShareValueMoreThanZero &&
      this.checkShareTypes &&
      this.convertibleLoanDeviateFromShareholderRights.value !== undefined &&
      this.convertibleLoanDuration.value === ConvertibleLoanDuration.MONTHS,
    );
  }

  get showReasonInput(): boolean {
    return Boolean(
      this.isConvertibleLoanValueMoreThanZero &&
      this.isConvertibleLoanCapitalShareValueMoreThanZero &&
      this.checkShareTypes &&
      this.convertibleLoanDeviateFromShareholderRights.value !== undefined &&
      ((this.convertibleLoanDuration.value === ConvertibleLoanDuration.MONTHS &&
        !this.invalidDurationMonths) ||
        this.convertibleLoanDuration.value === ConvertibleLoanDuration.UNTIL_GM),
    );
  }

  get shareTypes() {
    return this.context.initialState.shareTypes;
  }

  get isProjectLocked(): boolean {
    return this.context.isLocked;
  }

  get isValid(): boolean {
    return (
      this.convertibleLoanValue.isValid &&
      this.convertibleLoanCapitalShareValue.isValid &&
      this.convertibleLoanDeviateFromShareholderRights.isValid &&
      this.convertibleLoanNewShareType.isValid &&
      this.convertibleLoanNewShareTypeDescription.isValid &&
      this.convertibleLoanReason.isValid &&
      this.convertibleLoanDurationMonths.isValid
    );
  }
}
